import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CreditCard,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Edit3,
  Settings,
  ShieldCheck,
  X,
  Filter,
  Layers,
  ChevronRight,
} from "lucide-react";

import {
  useDeleteChargeTypeMutation,
  useGetChargeTypesQuery,
} from "../api/billingApi";

// Security & UI Patterns
import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import ChargeTypeModal from "./ChargeTypeModal";

/** * Aesthetic Tooltip Component */
function Tooltip({
  children,
  text,
}: {
  children: React.ReactNode;
  text: string;
}) {
  return (
    <div className="relative group flex items-center justify-center">
      {children}
      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-[60] pointer-events-none animate-in fade-in zoom-in-95 duration-100">
        <div className="bg-gray-900 text-white text-[9px] font-black uppercase tracking-[0.15em] px-2 py-1.5 rounded shadow-xl whitespace-nowrap border border-gray-700">
          {text}
        </div>
        <div className="w-2 h-2 bg-gray-900 rotate-45 mx-auto -mt-1 border-r border-b border-gray-700"></div>
      </div>
    </div>
  );
}

export default function ChargeTypesPage() {
  const [q, setQ] = useState("");
  const [modal, setModal] = useState<{ isOpen: boolean; id: number | null }>({
    isOpen: false,
    id: null,
  });

  const openNew = () => setModal({ isOpen: true, id: null });
  const openEdit = (id: number) => setModal({ isOpen: true, id });
  const closeModal = () => setModal({ isOpen: false, id: null });
  const {
    data = [],
    isLoading,
    refetch,
    isFetching,
  } = useGetChargeTypesQuery(undefined); // Fetch all to allow local filtering, or pass { q } if API handles it

  const [del, { isLoading: isDeleting }] = useDeleteChargeTypeMutation();
  const [editingId, setEditingId] = useState<number | null>(null);
  // Local filtering for instant UI response
  const filteredData = useMemo(() => {
    if (!q.trim()) return data;
    const term = q.toLowerCase();
    return data.filter(
      (x: any) =>
        x.name.toLowerCase().includes(term) ||
        x.code.toLowerCase().includes(term)
    );
  }, [data, q]);

  const onDelete = async (id: number) => {
    if (
      !window.confirm(
        "Confirm permanent deletion of this charge configuration?"
      )
    )
      return;
    try {
      await del(id).unwrap();
      refetch();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <PageGate perm={PERMS.billing.typeAccess}>
      <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">
        {/* 1. Industrial Header */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
                <CreditCard className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">
                  Charge Registry
                </h1>
                <p className="text-gray-400 text-sm mt-2 font-medium">
                  Management of institutional billing types and fee structures.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <AccessGate perm={PERMS.billing.typeCreate}>
                {/* <Link
                  to="/admin/billing/charge-types/new"
                  className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg active:scale-95"
                >
                  <Plus className="w-4 h-4" /> New Definition
                </Link> */}
                <button
                  onClick={openNew}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] ..."
                >
                  <Plus className="w-4 h-4" /> New Definition
                </button>
              </AccessGate>
              <Tooltip text="Refresh Sync">
                <button
                  onClick={() => refetch()}
                  className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
                >
                  <RefreshCw
                    className={`w-4 h-4 text-indigo-500 ${
                      isFetching ? "animate-spin" : ""
                    }`}
                  />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 2. Search Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm sticky top-6">
              <div className="flex items-center gap-2 mb-6 border-b border-gray-50 pb-4">
                <Settings className="w-4 h-4 text-indigo-500" />
                <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">
                  Registry Filter
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Search Node
                  </label>
                  <div className="relative">
                    <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-300" />
                    <input
                      type="text"
                      placeholder="Name or Code..."
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      className="w-full pl-5 border-b-2 border-gray-50 py-2 text-[11px] font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-50">
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">
                      Active Types
                    </div>
                    <div className="text-2xl font-black text-gray-900">
                      {filteredData.length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Main Data Matrix */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left bg-white border-b border-gray-50">
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Charge Identity
                      </th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Categorization
                      </th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">
                        Status
                      </th>
                      <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {isLoading ? (
                      <tr>
                        <td colSpan={4} className="py-20 text-center">
                          <RefreshCw className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
                          <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                            Loading Registry...
                          </span>
                        </td>
                      </tr>
                    ) : filteredData.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-20 text-center">
                          <Filter className="w-12 h-12 mx-auto text-gray-100 mb-4" />
                          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
                            No definitions found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((x: any) => (
                        <tr
                          key={x.id}
                          className="group hover:bg-gray-50/80 transition-all"
                        >
                          <td className="px-8 py-6">
                            <div className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">
                              {x.name}
                            </div>
                            <div className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-widest">
                              Code: {x.code}
                            </div>
                          </td>
                          <td className="px-8 py-6">
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                              {x.type.replace("_", " ")}
                            </span>
                          </td>
                          <td className="px-8 py-6 text-center">
                            <div
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[9px] font-black uppercase tracking-widest ${
                                x.is_active
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                  : "bg-gray-50 text-gray-400 border-gray-100"
                              }`}
                            >
                              <div
                                className={`w-1.5 h-1.5 rounded-full ${
                                  x.is_active ? "bg-emerald-500" : "bg-gray-300"
                                }`}
                              />
                              {x.is_active ? "Active" : "Disabled"}
                            </div>
                          </td>
                          <td className="px-8 py-6 text-right">
                            <div className="flex justify-end gap-2">
                              <AccessGate perm={PERMS.billing.typeUpdate}>
                                <Tooltip text="Modify Definition">
                                  {/* <Link
                                    to={`/admin/billing/charge-types/${x.id}/edit`}
                                    className="p-2.5 bg-white border border-gray-100 rounded-xl text-gray-400 hover:text-indigo-600 hover:border-indigo-100 transition-all shadow-sm"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </Link> */}

                                  {/* <button
                                    onClick={() => setEditingId(x.id)} // Open modal instead of Link
                                    className="p-2.5 bg-white border border-gray-100 rounded-xl text-gray-400 hover:text-indigo-600 hover:border-indigo-100 transition-all shadow-sm"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button> */}
                                  <button
                                    onClick={() => openEdit(x.id)}
                                    className="p-2.5 bg-white border border-gray-100 rounded-xl text-gray-400 hover:text-indigo-600 hover:border-indigo-100 transition-all shadow-sm"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                </Tooltip>
                              </AccessGate>
                              <AccessGate perm={PERMS.billing.typeDelete}>
                                <Tooltip text="Purge Node">
                                  <button
                                    onClick={() => onDelete(x.id)}
                                    className="p-2.5 bg-white border border-gray-100 rounded-xl text-gray-400 hover:text-rose-600 hover:border-rose-100 transition-all shadow-sm"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </Tooltip>
                              </AccessGate>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {modal.isOpen && (
                  <ChargeTypeModal
                    id={modal.id}
                    onClose={closeModal}
                    onSuccess={() => {
                      closeModal();
                      refetch();
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 4. Policy Footer */}
        <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center gap-10 shadow-2xl shadow-gray-200 mt-10">
          <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
            <ShieldCheck className="w-10 h-10 text-indigo-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-3">
              Fiscal Governance
            </h4>
            <p className="text-sm text-gray-400 leading-relaxed font-medium">
              Charge type definitions directly impact{" "}
              <span className="text-white">REVENUE RECOGNITION</span>. Ensure
              codes match the primary accounting ledger. Modifications to active
              charge types will affect all
              <span className="text-white"> NEW INVOICES</span> generated from
              the point of commit.
            </p>
          </div>
        </div>
      </div>
    </PageGate>
  );
}
